import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  radiosVerificadas,
  categoriasVerificacion,
  beneficiosVerificacion,
  ubicacionesInsignia,
  reglasRadiosVerificadas,
  statsRadiosVerificadas,
  getRadiosPorCategoria,
  getTopRadiosVerificadas,
  getRadiosEnTendencia,
  getRadiosEnVivo,
  formatearNumero,
  type CategoriaRadioVerificada,
  type RadioVerificada,
} from '@/mocks/radiosVerificadas';

function CategoriaBadge({ categoria }: { categoria: CategoriaRadioVerificada }) {
  const c = categoriasVerificacion.find(cat => cat.id === categoria);
  if (!c) return null;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold ${c.bg} ${c.color} border ${c.borde}`}>
      <i className={`${c.icono} text-[8px]`}></i>
      {c.label}
    </span>
  );
}

function RadioVerificadaCard({ radio, size }: { radio: RadioVerificada; size?: 'compact' | 'featured' }) {
  const cat = categoriasVerificacion.find(c => c.id === radio.categoriaVerificacion);
  const isFeatured = size === 'featured';

  return (
    <Link
      to="/radios-verificadas"
      className={`group block rounded-2xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-amber-300/60 transition-all duration-300 ${isFeatured ? '' : ''}`}
    >
      <div className={`relative ${isFeatured ? 'h-48' : 'h-36'} overflow-hidden`}>
        <img
          src={radio.imagenUrl}
          alt={radio.nombre}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50/90 via-background-50/30 to-transparent"></div>

        <div className="absolute top-3 left-3 flex items-center gap-2">
          {radio.estaEnVivo && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/90 text-white text-[9px] font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
              EN VIVO
            </span>
          )}
          <CategoriaBadge categoria={radio.categoriaVerificacion} />
        </div>

        <div className="absolute top-3 right-3">
          <img src={radio.logoUrl} alt="" className="w-10 h-10 rounded-xl object-cover border-2 border-white/80 shadow-sm" />
        </div>

        {radio.enTendencia && (
          <div className="absolute bottom-3 left-3">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/90 text-white text-[9px] font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
              TENDENCIA
            </span>
          </div>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h4 className="text-sm font-bold text-foreground-950 line-clamp-1">{radio.nombre}</h4>
          {cat && (
            <span className={`shrink-0 w-6 h-6 rounded-full ${cat.bg} flex items-center justify-center`}>
              <i className={`${cat.icono} text-xs ${cat.color}`}></i>
            </span>
          )}
        </div>
        <p className="text-[10px] text-foreground-500 mb-2">{radio.frecuencia} · {radio.ciudad}, {radio.pais}</p>
        <p className="text-[10px] text-foreground-600 line-clamp-2 mb-3 leading-relaxed">{radio.descripcion}</p>

        <div className="flex items-center justify-between text-[9px]">
          <div className="flex items-center gap-3 text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-headphone-line text-[8px]"></i>
              {formatearNumero(radio.oyentes)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-group-line text-[8px]"></i>
              {formatearNumero(radio.seguidores)}
            </span>
          </div>
          <span className="text-amber-600 font-medium flex items-center gap-0.5">
            <i className="ri-star-fill text-[8px]"></i>
            {radio.rating}
          </span>
        </div>

        {isFeatured && (
          <div className="flex flex-wrap gap-1 mt-3">
            {radio.tags.slice(0, 3).map(tag => (
              <span key={tag} className="px-1.5 py-0.5 rounded-md bg-background-100 text-[8px] text-foreground-400 border border-background-200/50">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}

export default function RadiosVerificadasSection() {
  const [activeTab, setActiveTab] = useState<'carrusel' | 'categorias' | 'beneficios' | 'reglas'>('carrusel');
  const [categoriaFilter, setCategoriaFilter] = useState<CategoriaRadioVerificada | 'todas'>('todas');

  const topRadios = getTopRadiosVerificadas(10);
  const radiosTrending = getRadiosEnTendencia();
  const radiosEnVivo = getRadiosEnVivo();
  const radiosFiltradas = categoriaFilter === 'todas' ? radiosVerificadas : getRadiosPorCategoria(categoriaFilter);

  const tabs = [
    { id: 'carrusel' as const, label: 'Carrusel', icon: 'ri-slideshow-3-line' },
    { id: 'categorias' as const, label: 'Categorías', icon: 'ri-stack-line' },
    { id: 'beneficios' as const, label: 'Beneficios', icon: 'ri-star-line' },
    { id: 'reglas' as const, label: 'Reglas', icon: 'ri-scales-line' },
  ];

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
            RADIOS VERIFICADAS Y DESTACADAS
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Las Mejores Radios de <span className="text-amber-600">RFW</span>
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Cinco categorías que reconocen la calidad, trayectoria y comunidad de las radios más destacadas de la plataforma. Solo los Super Admins pueden otorgar estas insignias.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {[
            { label: 'Total Verificadas', value: statsRadiosVerificadas.totalRadiosVerificadas.toString(), icon: 'ri-verified-badge-line', color: 'text-sky-600 bg-sky-50' },
            { label: 'Elite', value: statsRadiosVerificadas.porCategoria.elite.toString(), icon: 'ri-sparkling-2-line', color: 'text-violet-600 bg-violet-50' },
            { label: 'Oficiales', value: statsRadiosVerificadas.porCategoria.oficial.toString(), icon: 'ri-shield-check-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Oyentes Totales', value: formatearNumero(statsRadiosVerificadas.totalOyentes), icon: 'ri-headphone-line', color: 'text-rose-600 bg-rose-50' },
            { label: 'Tasa Aprobación', value: statsRadiosVerificadas.tasaAprobacion, icon: 'ri-check-double-line', color: 'text-amber-600 bg-amber-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-center mb-6">
          <div className="inline-flex bg-background-50 rounded-full p-1 border border-background-200/70">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {activeTab === 'carrusel' && (
          <div className="space-y-8">
            <div className="flex flex-wrap items-center gap-1.5 justify-center">
              <button
                onClick={() => setCategoriaFilter('todas')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                  categoriaFilter === 'todas'
                    ? 'bg-primary-500 text-white border-primary-500'
                    : 'bg-background-50 text-foreground-500 border-background-200 hover:border-foreground-300'
                }`}
              >
                <i className="ri-list-check-3 text-[9px]"></i>
                Todas
              </button>
              {categoriasVerificacion.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setCategoriaFilter(cat.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                    categoriaFilter === cat.id
                      ? `${cat.bg} ${cat.color} ${cat.borde}`
                      : 'bg-background-50 text-foreground-500 border-background-200 hover:border-foreground-300'
                  }`}
                >
                  <i className={`${cat.icono} text-[9px]`}></i>
                  {cat.label}
                  <span className="text-[9px] opacity-60">({statsRadiosVerificadas.porCategoria[cat.id]})</span>
                </button>
              ))}
            </div>

            {categoriaFilter === 'todas' && (
              <div className="space-y-6">
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-6 h-6 rounded-full bg-rose-100 flex items-center justify-center">
                      <i className="ri-fire-line text-rose-600 text-xs"></i>
                    </div>
                    <h3 className="text-sm font-bold text-foreground-950">En Tendencia</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {radiosTrending.slice(0, 2).map(radio => (
                      <RadioVerificadaCard key={radio.id} radio={radio} size="featured" />
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center">
                      <i className="ri-live-line text-red-600 text-xs"></i>
                    </div>
                    <h3 className="text-sm font-bold text-foreground-950">En Vivo Ahora</h3>
                  </div>
                  <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-thin" style={{ scrollSnapType: 'x mandatory' }}>
                    {radiosEnVivo.slice(0, 8).map(radio => (
                      <div key={radio.id} className="flex-shrink-0 w-[280px]" style={{ scrollSnapAlign: 'start' }}>
                        <RadioVerificadaCard radio={radio} />
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center">
                      <i className="ri-trophy-line text-amber-600 text-xs"></i>
                    </div>
                    <h3 className="text-sm font-bold text-foreground-950">Top 10 Radios Verificadas</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {topRadios.map(radio => (
                      <RadioVerificadaCard key={radio.id} radio={radio} />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {categoriaFilter !== 'todas' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {radiosFiltradas.map(radio => (
                  <RadioVerificadaCard key={radio.id} radio={radio} />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'categorias' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {categoriasVerificacion.map((cat) => {
                const count = statsRadiosVerificadas.porCategoria[cat.id];
                const radios = getRadiosPorCategoria(cat.id);
                return (
                  <div key={cat.id} className={`p-5 rounded-2xl border-2 ${cat.borde} bg-background-50 text-center relative overflow-hidden`}>
                    <div className={`absolute top-0 left-0 right-0 h-1 ${cat.bg}`}></div>
                    <div className={`w-14 h-14 rounded-full ${cat.bg} flex items-center justify-center mx-auto mb-3 mt-3`}>
                      <i className={`${cat.icono} text-2xl ${cat.color}`}></i>
                    </div>
                    <p className="text-base font-extrabold text-foreground-950 mb-1">{cat.label}</p>
                    <p className="text-[10px] text-foreground-500 mb-2 leading-relaxed">{cat.descripcion}</p>
                    <span className={`inline-flex items-center gap-1 text-xs font-bold ${cat.color}`}>
                      <i className="ri-radio-line text-[10px]"></i>
                      {count} radios
                    </span>
                    <div className="flex -space-x-2 justify-center mt-3">
                      {radios.slice(0, 4).map(r => (
                        <img key={r.id} src={r.logoUrl} alt={r.nombre} className="w-8 h-8 rounded-full object-cover border-2 border-background-50" title={r.nombre} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
              {categoriasVerificacion.map((cat) => {
                const count = statsRadiosVerificadas.porCategoria[cat.id];
                const radios = getRadiosPorCategoria(cat.id).slice(0, 3);
                return (
                  <div key={cat.id} className={`p-4 rounded-xl bg-background-50 border ${cat.borde}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-8 h-8 rounded-full ${cat.bg} flex items-center justify-center`}>
                        <i className={`${cat.icono} text-sm ${cat.color}`}></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-foreground-950">{cat.label}</p>
                        <p className="text-[10px] text-foreground-500">{cat.descripcion}</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {radios.map(r => (
                        <div key={r.id} className="flex items-center gap-2 text-[10px]">
                          <img src={r.logoUrl} alt="" className="w-5 h-5 rounded-full object-cover" />
                          <span className="text-foreground-700 font-medium truncate">{r.nombre}</span>
                          <span className="text-foreground-400 ml-auto">{r.frecuencia}</span>
                        </div>
                      ))}
                      {count > 3 && (
                        <p className="text-[10px] text-foreground-400 text-center pt-1">+{count - 3} radios más</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'beneficios' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-5">
              {beneficiosVerificacion.map((beneficio) => (
                <div key={beneficio.id} className={`p-5 rounded-2xl ${beneficio.bgColor} border border-background-200/50`}>
                  <div className="w-10 h-10 rounded-full bg-background-50 flex items-center justify-center mx-auto mb-3">
                    <i className={`${beneficio.icono} text-xl ${beneficio.color}`}></i>
                  </div>
                  <p className="text-xs font-bold text-foreground-950 mb-1.5 text-center">{beneficio.titulo}</p>
                  <p className="text-[10px] text-foreground-500 leading-relaxed text-center">{beneficio.descripcion}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              {ubicacionesInsignia.map((ubi) => (
                <div key={ubi.id} className="p-3 rounded-xl bg-background-50 border border-background-200/70 text-center">
                  <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-1.5">
                    <i className={`${ubi.icono} text-sm text-emerald-600`}></i>
                  </div>
                  <p className="text-[11px] font-bold text-foreground-950">{ubi.nombre}</p>
                  <p className="text-[9px] text-foreground-400">{ubi.descripcion}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'reglas' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {reglasRadiosVerificadas.map((regla) => (
                <div key={regla.id} className={`p-5 rounded-2xl bg-gradient-to-br ${regla.gradiente} bg-opacity-10 border ${regla.colorBorde} bg-background-50`}>
                  <div className="w-10 h-10 rounded-full bg-background-50 flex items-center justify-center mb-3">
                    <i className={`${regla.icono} text-lg text-foreground-950`}></i>
                  </div>
                  <p className="text-sm font-bold text-foreground-950 mb-1.5">{regla.titulo}</p>
                  <p className="text-xs text-foreground-600 leading-relaxed">{regla.descripcion}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="text-center mt-10">
          <Link
            to="/radios-verificadas"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-600 hover:to-rose-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-verified-badge-line"></i>
            Ver Todas las Radios Verificadas
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="mt-8 p-5 rounded-2xl bg-amber-50 border border-amber-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-admin-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            Solo SA puede otorgar verificaciones.
          </p>
          <p className="text-xs text-foreground-600 max-w-lg mx-auto">
            En RFW las insignias de Radio Verificada, Destacada, Premium, Elite y Oficial solo pueden ser otorgadas por los Super Admins. No se compran, no se regalan y no son automáticas. Cada insignia representa una evaluación rigurosa de calidad, trayectoria y comunidad.
          </p>
        </div>
      </div>
    </section>
  );
}